import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { standaloneFieldSx, standaloneFloatingSelectSx } from '../../common/fieldStyles';
import { formatCurrency } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';
import DialogForm from '../../component/dialog/DialogForm';

// Lançamento manual de conta a pagar: POST /transactions/payables
// (`fv-api/transaction/payable_creator.go`). Nasce sempre como despesa Pendente,
// sem movimentar o caixa; a baixa é feita depois na própria tela.
//   body: { supplier_id?, description, amount ("1.234,56"), due_date (YYYY-MM-DD) }

const todayIso = () => dayjs().format('YYYY-MM-DD');

const parseAmountCents = (value) => Number(String(value || '').replace(/\D/g, '')) || 0;

export default function PayableCreateDialog({ open, suppliers, onClose, onSuccess, onError }) {
    const [supplierId, setSupplierId] = useState('');
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [dueDate, setDueDate] = useState(todayIso());
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // Reinicia o formulário a cada abertura.
    useEffect(() => {
        if (!open) return;
        setSupplierId('');
        setDescription('');
        setAmount('');
        setDueDate(todayIso());
        setErrors({});
    }, [open]);

    const validate = () => {
        const next = {};
        if (!description.trim()) next.description = 'Descrição é obrigatória';
        if (parseAmountCents(amount) <= 0) next.amount = 'Valor deve ser maior que zero';
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate) || !dayjs(dueDate).isValid()) next.dueDate = 'Data de vencimento é obrigatória';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (saving || !validate()) return;
        setSaving(true);
        try {
            await httpClient.post('/transactions/payables', {
                supplier_id: supplierId || undefined,
                description: description.trim(),
                amount,
                due_date: dueDate,
            });
            onSuccess();
        } catch (error) {
            const data = error?.response?.data;
            const text = typeof data === 'string' ? data.trim() : (data?.error || data?.message || '');
            onError(text || 'Não foi possível lançar a conta a pagar.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <DialogForm
            open={open}
            iconType="info"
            title="Nova conta a pagar"
            subtitle="Lança uma despesa pendente. O caixa só é movimentado ao dar baixa."
            confirmButtonText="Salvar"
            cancelButtonText="Cancelar"
            loading={saving}
            loadingText="Salvando…"
            onClose={() => { if (!saving) onClose(); }}
            onSubmit={() => { void handleSubmit(); }}
        >
            <TextField
                select
                fullWidth
                label="Fornecedor (opcional)"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
                sx={standaloneFloatingSelectSx}
            >
                <MenuItem value="">Nenhum</MenuItem>
                {suppliers.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
            </TextField>
            <TextField
                fullWidth
                label="Descrição *"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                inputProps={{ maxLength: 255 }}
                error={Boolean(errors.description)}
                helperText={errors.description}
                variant="outlined"
                sx={standaloneFieldSx}
            />
            <TextField
                fullWidth
                label="Valor (R$) *"
                value={amount}
                onChange={(e) => setAmount(formatCurrency(e.target.value))}
                inputProps={{ inputMode: 'numeric' }}
                error={Boolean(errors.amount)}
                helperText={errors.amount}
                variant="outlined"
                sx={standaloneFieldSx}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <DatePicker
                    label="Vencimento *"
                    format="DD/MM/YYYY"
                    value={dueDate ? dayjs(dueDate) : null}
                    onChange={(v) => setDueDate(v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            error: Boolean(errors.dueDate),
                            helperText: errors.dueDate,
                            sx: {
                                ...standaloneFieldSx,
                                '& .MuiPickersOutlinedInput-root': {
                                    height: '52px',
                                    borderRadius: '12px',
                                    backgroundColor: 'background.subtle',
                                },
                                '& .MuiPickersInputBase-sectionsContainer': {
                                    fontSize: '13.5px',
                                    padding: '21px 0 7px 0',
                                },
                            },
                        },
                    }}
                />
            </LocalizationProvider>
        </DialogForm>
    );
}
